import moment from 'moment';
import { colors } from '../../colors';

/**
 * Vertical striped markers drawn on the balance charts.
 *
 * `FEATURE_REFERENCE_LINES` sit on a fixed date — the day a feature went live —
 * and are the same on every chart. `EXODUS_REFERENCE_LINE` has no date: it is
 * drawn at the start of whatever x domain the chart itself is showing.
 * `CONFIG_CHANGE_REFERENCE_LINE` is dated per chart: the latest config change of
 * the bot trading the chart's baseAsset (`GET /client/configChangedAt`).
 */
export type ChartReferenceLine = {
  key: string;
  label: string;
  patternId: string;
  color: string;
};

export type DatedChartReferenceLine = ChartReferenceLine & {
  timestamp: number;
};

export const TPT_REFERENCE_LINE: DatedChartReferenceLine = {
  key: 'tpt',
  label: 'TPT',
  timestamp: moment('2026-04-12').valueOf(),
  patternId: 'orangeStripes',
  color: colors.orange,
};

export const MRC_REFERENCE_LINE: DatedChartReferenceLine = {
  key: 'mrc',
  label: 'MRC',
  timestamp: moment('2026-07-03').valueOf(),
  patternId: 'purpleStripes',
  color: colors.purple,
};

export const SUB_1D_REFERENCE_LINE: DatedChartReferenceLine = {
  key: 'sub1d',
  label: 'SUB-1D',
  timestamp: moment('2026-10-06').valueOf(),
  patternId: 'cyanStripes',
  color: colors.cyan,
};

export const FEATURE_REFERENCE_LINES: DatedChartReferenceLine[] = [
  TPT_REFERENCE_LINE,
  MRC_REFERENCE_LINE,
  SUB_1D_REFERENCE_LINE,
];

export const EXODUS_REFERENCE_LINE: ChartReferenceLine = {
  key: 'exodus',
  label: 'EXODUS',
  patternId: 'lightBlueStripes',
  color: colors.ligthBlue,
};

export const CONFIG_CHANGE_REFERENCE_LINE: ChartReferenceLine = {
  key: 'configChange',
  label: 'CONFIG',
  patternId: 'goldStripes',
  color: colors.gold,
};

/** The two-point dataset of a vertical line spanning the chart's y domain. */
export const buildReferenceLineData = (
  x: number,
  yBottom: number,
  yTop: number,
) => [
  { x, y: yBottom },
  { x, y: yTop },
];

/**
 * The diagonal stripe patterns the reference lines are stroked with. Render
 * inside the chart's own `<defs>` — every chart that draws a reference line
 * needs them, and identical ids across charts are harmless.
 */
export const ReferenceLinePatterns = () => (
  <>
    {[
      ...FEATURE_REFERENCE_LINES,
      EXODUS_REFERENCE_LINE,
      CONFIG_CHANGE_REFERENCE_LINE,
    ].map(({ patternId, color }) => (
      <pattern
        key={patternId}
        id={patternId}
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
        patternTransform="rotate(45)"
      >
        <line
          x1="0"
          y1="0"
          x2="0"
          y2="4"
          stroke={color}
          strokeWidth="2"
          opacity="0.7"
        />
      </pattern>
    ))}
  </>
);
